import React from 'react'
import { Block, Container } from '../../components/basic/layout'
import { H1, H2, H3, H4, H5, P } from '../../components/basic/text'
import * as Colors from '../../constants/colors'

const DocsPage = () => {
	return (
		<Container>
            <Block padding="40px 20px" background={Colors.White}>
                <H1>Docs</H1>
                <P>
                    This page serves as the location for all documentation regarding this project. 
                    This includes Architectural documents, explanations for business logic, and 
                    quick start guides for new developers. 
                </P>
            </Block>
            <Block padding="40px 20px" background={Colors.Gray_100}>
                <H2>
                    Table of contents
                </H2>
                <Block>
                    <P>Architecture</P>
                </Block>
            </Block>
            <Block padding="40px 20px" background={Colors.White}>
                <H2>Architecture</H2>
                <H3 margin="20px 0 0 0">
                    jcom-react
                </H3>
                <P margin="10px 0 0 0">
                    jcom-react is an architecture designed around reusable patterns utilizing the Reactjs 
                    Javascript framework. The core philosophy of jcom-react is to create reusable components 
                    that can be shared downward to child projects that can consume the components and therefore 
                    forego having to create their own unique components on top of the standard jcom components. 
                </P>
                <P margin="10px 0 0 0">
                    This architectural pattern is broken down into 3 levels of inheritance: jcom-core being the 
                    top level repository that contains standard jcom reusable components that all jcom-react 
                    repositories consume, such as buttons and colors. 
                </P>
                <P margin="10px 0 0 0">
                    Second there is the optional middle 
                    level repository that is only required on some repositories, this middle level repository 
                    is more specific than jcom-core but contains components and functionality that can be 
                    shared across many other repositories. A good example of the middle level repository is 
                    the jcom-builder repository. This project inherits the core components of jcom-core, but also 
                    has lots of business logic and reusable components that other builder repositories could use, 
                    such as upsell-package-builder and jacket-builder.
                </P>
                <P margin="10px 0 0 0">
                    The third and bottom level repository is 
                    a repository that is not optional and is the actual repository that the front end user sees. 
                    This is where your jcom-components and your optional middle level repo come together to 
                    create a front end experience that the user sees. This bottom level repo always imports the 
                    jcom-core module, and sometimes imports a middle level module based on project requirements. 
                    A good example of a bottom level repository would be the upsell-package-builder and the 
                    jacket-builder repositories. Both of those repositories use the jcom-core module and they 
                    import the jcom-builder module as these repositories are builders.
                </P>
                <H3 margin="20px 0 0 0">
                    Overview
                </H3>
                <P margin="10px 0 0 0">
                    Top Level: jcom-core. Core components and functionality used across all of jcom-react repos.
                </P>
                <P margin="10px 0 0 0">
                    Middle Level: jcom-builder (optional). Reusable components for specific types of repos.
                </P>
                <P margin="10px 0 0 0">
                    Bottom Level: upsell-package-builder, jacket-builder. The front facing repository 
                    where projects live.
                </P>
                <H3 margin="20px 0 0 0">
                    jcom-core
                </H3>
                <H3 margin="20px 0 0 0">
                    jcom-builder
                </H3>
                <P margin="10px 0 0 0">
                    The jcom-builder repository contains all of the base components for projects that are jcom-builders. 
                    The jcom-builder repository also contains a lot of business logic that is reused and standardized 
                    throughout the builder projects, such as step and substep navigation, choosing products, calculating 
                    price, etc. The jcom-builder repository is a npm module that is installed with every builder project.
                </P>
                <H4 margin="15px 0 0 0">
                    Builder structure
                </H4>
                <P margin="10px 0 0 0">
                    /build - these are the build files generated by running npm run build (consult 
                    create-react-app for more info).
                </P>
                <P margin="10px 0 0 0">
                    /node_modules - all of the node modules install to make this repository run, these may contain 
                    functionality or components this repo consumes.
                </P>
                <P margin="10px 0 0 0">
                    /public - public html the browsers crawl, this will give basic project information such as 
                    html title and the "html" base tag as well as the "content" tag the react app is instantiated 
                    inside of.
                </P>
                <P margin="10px 0 0 0">
                    /src - this is where all of the functionality of the project lives. This will be explained 
                    in further detail below.
                </P>
                <H5  margin="10px 0 0 0">
                    Inside the src directory
                </H5>
                <P margin="10px 0 0 0">
                    /api - this is where all of the http fetch calls for your project exist. Any function that 
                    either sends or receives data from an API call lives here.
                </P>
                <P margin="10px 0 0 0">
                    /app - your app structure for your repository lives here. This will show you where all of 
                    the context inheritance lives, where the routes lives, where pages live. 
                </P>
                <P margin="10px 0 0 0">
                    /components - reusable components used in this repository or child repositories live here. 
                    Components are broken down into two categories: advanced and basic. Advanced components are 
                    components that contain specific business logic related functionality and can tie in with 
                    the app context. Basic components are components that have ALL functionality passed into 
                    them and cannot contain or manipulate app context by themselves.
                </P>
                <P margin="10px 0 0 0">
                    /contants - these are exported constants your app relies on. These constants are things such 
                    as routes, app specific colors, and other strings or variables that may be used throughout the 
                    application.
                </P>
                <P margin="10px 0 0 0">
                    /context - this is your app context or app state. There can be many contexts within the app, 
                    and all of them live here.
                </P>
                <P margin="10px 0 0 0">
                    /data - all data inherited from the page this repository sits in, lives here. This is data 
                    passed to the app outside of an api call. This includes things existing on the window object 
                    or document object. This includes things such as google analytics or data being passed to 
                    the app through the window.
                </P>
                <P margin="10px 0 0 0">
                    /pages - all of your pages that exist in this repository live here. This is where your core 
                    functionality is going to live. Pages all have an index file that is read and contains the 
                    page component. Pages can also have components that are specific to themselves that live 
                    within their own folder. These are components that are not reused in other pages in the 
                    repository. 
                </P>
            </Block>
		</Container>
	)
}

export default DocsPage
